import { useCallback, useEffect, useState } from "react";
import { Trash2, RotateCcw, Search, SearchCheck, ShieldCheck, Eraser } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errMsg, fmtDate } from "@/lib/api";
import { LeadUpload } from "./LeadUpload";
import { AddLeadDialog } from "./AddLeadDialog";
import { SendToSelectedDialog } from "./SendToSelectedDialog";
import { StatusBadge } from "./StatusBadge";
import { ValidationBadge, isFlagged, runValidation } from "./validation";

export function LeadsPanel({ slot, onChange, onFindEmails, invalidCount = 0 }) {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sel, setSel] = useState([]);

  const load = useCallback(() => {
    api.get(`/projects/${slot}/leads`, { params: { search, status: status === "all" ? "" : status } })
      .then((r) => setLeads(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [slot, search, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const refresh = () => { load(); onChange(); setSel([]); };
  const bulk = async (action) => {
    if (action === "delete" && !window.confirm(`Delete ${sel.length} lead(s)?`)) return;
    try {
      await api.post(`/projects/${slot}/leads/${action}`, { ids: sel });
      toast.success(action === "delete" ? "Leads deleted" : "Leads marked as not sent");
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cleanInvalid = async () => {
    if (!window.confirm(`Delete all ${invalidCount} invalid lead(s) from this project? This cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/projects/${slot}/leads/clean-invalid`);
      toast.success(`Removed ${data.deleted} invalid lead(s)`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const validateSel = async () => {
    const res = await runValidation(leads.filter((l) => sel.includes(l.id)).map((l) => l.email));
    if (res) { load(); onChange(); }
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSel = leads.length > 0 && sel.length === leads.length;

  return (
    <div className="space-y-5" data-testid="leads-panel">
      <div className="grid lg:grid-cols-[1fr_auto_auto] gap-4 items-center">
        <LeadUpload slot={slot} onDone={refresh} />
        <Button variant="secondary" onClick={onFindEmails} data-testid="leads-find-emails-button"><SearchCheck className="h-4 w-4 mr-1.5" />Find emails</Button>
        <AddLeadDialog slot={slot} onDone={refresh} />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search email, name or company" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="lead-table-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44" data-testid="lead-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "new", "sent", "failed", "bounced", "unsubscribed"].map((s) => <SelectItem key={s} value={s}>{s === "new" ? "Not sent" : s[0].toUpperCase() + s.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
        {sel.length > 0 && (
          <>
            <SendToSelectedDialog slot={slot} leadIds={sel} onDone={refresh} />
            <Button variant="secondary" size="sm" onClick={validateSel} data-testid="lead-bulk-validate-button"><ShieldCheck className="h-4 w-4 mr-1.5" />Validate {sel.length}</Button>
            <Button variant="secondary" size="sm" onClick={() => bulk("reset")} data-testid="lead-bulk-reset-button"><RotateCcw className="h-4 w-4 mr-1.5" />Mark not sent</Button>
            <Button variant="destructive" size="sm" onClick={() => bulk("delete")} data-testid="lead-bulk-delete-button"><Trash2 className="h-4 w-4 mr-1.5" />Delete {sel.length}</Button>
          </>
        )}
        {invalidCount > 0 && (
          <Button variant="destructive" size="sm" onClick={cleanInvalid} data-testid="lead-clean-invalid-button">
            <Eraser className="h-4 w-4 mr-1.5" />Remove {invalidCount} invalid
          </Button>
        )}
        <span className="text-xs font-mono text-muted-foreground ml-auto" data-testid="lead-count">{leads.length} shown</span>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-10"><Checkbox checked={allSel} onCheckedChange={() => setSel(allSel ? [] : leads.map((l) => l.id))} data-testid="lead-select-all" /></TableHead>
              <TableHead>Email</TableHead><TableHead>Name</TableHead><TableHead>Company</TableHead>
              <TableHead>Title</TableHead><TableHead>Status</TableHead><TableHead>Validation</TableHead><TableHead>Last sent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-14 text-muted-foreground" data-testid="leads-empty">{search || status !== "all" ? "No leads match this search or filter." : "No leads yet. Upload a file or add one manually."}</TableCell></TableRow>
            )}
            {leads.map((l) => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                <TableCell><Checkbox checked={sel.includes(l.id)} onCheckedChange={() => toggle(l.id)} data-testid={`lead-select-${l.id}`} /></TableCell>
                <TableCell className={`font-mono text-xs ${isFlagged(l.validation) ? "text-rose-700 font-semibold" : ""}`} data-testid={`lead-email-${l.id}`}>{l.email}</TableCell>
                <TableCell>{l.name || "—"}</TableCell>
                <TableCell>{l.company || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{l.title || "—"}</TableCell>
                <TableCell><StatusBadge status={l.status} /></TableCell>
                <TableCell><ValidationBadge v={l.validation} testId={`lead-validation-${l.id}`} /></TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(l.last_sent_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
